/**
 * The persisted batch document — one brew of one recipe, and the permanent
 * record of what actually happened (PLAN §3 V1: "batch logging: actuals vs.
 * predicted", "recipe versioning/snapshots").
 *
 * ## The one idea this document exists for
 *
 * A batch carries a **frozen copy of the recipe it was brewed from**, not a
 * pointer to it. `recipeSnapshot` is a whole {@link RecipeDoc}; `equipmentSnapshot`
 * is a whole {@link EquipmentProfileDoc}. Editing the source recipe afterwards —
 * or deleting it, or recalibrating the equipment profile — cannot move a single
 * number on a batch that has already been brewed. "Brewed as v3 on 2026-09-01"
 * has to *mean* something, and the only way to make it mean something is to
 * store v3.
 *
 * This is the same snapshot rule PLAN §6 applies to ingredients, applied one
 * level up. Note that it deliberately **inverts** the equipment decision made
 * for recipes: a recipe *references* its equipment profile (a recalibrated
 * boil-off should apply to every recipe on that system at once), but a batch
 * *snapshots* it (the boil-off that day was what it was). Both follow from the
 * same principle — a plan tracks the system as it is, a record tracks the system
 * as it was.
 *
 * `recipeId` is kept alongside the snapshot, nullable, purely as a back-link for
 * "show me every batch of this recipe". It is provenance, exactly like
 * {@link CatalogRef}: nothing dereferences it to compute anything, and a deleted
 * recipe leaves the batch entirely intact.
 *
 * ## Time
 *
 * Every timestamp here is an **instant** (ms since epoch) supplied by the
 * caller, never read from a clock — this package has none. Timer state is
 * likewise stored as *started-at anchors*, never as "23:14 remaining": a
 * remaining-duration would be wrong the moment the tab is backgrounded, the
 * device sleeps, or the state syncs to the brewer's other device. Remaining time
 * is a render-time subtraction from an anchor, so it survives reload and sync
 * for free.
 */

import { z } from 'zod';
import { BATCH_SCHEMA_VERSION } from '../version';
import {
  acidIdSchema,
  currencyCodeSchema,
  documentIdSchema,
  isoDateSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  type PackagingMethod,
  type PrimingSugarId,
  packagingMethodSchema,
  percentSchema,
  primingSugarSchema,
  specificGravitySchema,
  timestampSchema,
} from './common';
import { type EquipmentProfileDoc, equipmentProfileDocSchema } from './equipment';
import { RECIPE_YEAST_STARTER_FIELD } from './ingredients';
import { inventoryKindSchema } from './inventory';
import { type RecipeDoc, recipeDocSchema } from './recipe';

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/**
 * A specific gravity reading.
 *
 * Defined in `common.ts` and re-exported here, where it was first written and
 * where every reader of this module still looks for it. It moved because the
 * recipe document now states a *target* gravity and this module imports that
 * document, so the definition could not stay on this side of the arrow.
 */
export { specificGravitySchema };

/** A measured vessel volume, **litres**. */
export const measuredVolumeSchema = nonNegativeSchema.max(10_000);

/** A measured temperature, **°C**. */
export const measuredTempCSchema = z.number().finite().min(-30).max(120);

/**
 * A pH read off a meter.
 *
 * The full 3–11 a meter can produce rather than the 5-ish a kettle should sit
 * at: this records what the instrument said, and a reading of 6.2 out of a
 * badly-lautered kettle is a fact worth keeping, not a typo worth refusing.
 */
export const measuredPhSchema = z.number().finite().min(3).max(11);

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * Where a batch is in its life.
 *
 * Ordered as the brewer moves through it, which is what the detail page's
 * stepper renders. `archived` is deliberately last and off the happy path: it
 * is "filed away", reachable from anywhere, not a seventh brewing stage.
 */
export const batchStatusSchema = z.enum([
  'planned',
  'brew-day',
  'fermenting',
  'conditioning',
  'packaged',
  'archived',
]);

/** @see batchStatusSchema */
export type BatchStatus = z.infer<typeof batchStatusSchema>;

/** Every status, in stepper order. */
export const BATCH_STATUSES: readonly BatchStatus[] = batchStatusSchema.options;

/** The statuses that form the linear brew-day → packaged progression. */
export const BATCH_PROGRESS_STATUSES: readonly BatchStatus[] = [
  'planned',
  'brew-day',
  'fermenting',
  'conditioning',
  'packaged',
];

// ---------------------------------------------------------------------------
// Measurements
// ---------------------------------------------------------------------------

/**
 * A gravity-and-volume pair read at one point in the waterfall.
 *
 * Both halves are independently nullable because they are independently
 * measured: a brewer routinely takes a refractometer reading and never touches
 * a dipstick, and half a data point is still worth recording.
 */
export const batchGravityVolumeSchema = z.strictObject({
  /** Specific gravity at this point, or `null` if not measured. */
  sg: specificGravitySchema.nullable(),
  /** Volume at this point, **litres**, or `null` if not measured. */
  volumeL: measuredVolumeSchema.nullable(),
});

/** @see batchGravityVolumeSchema */
export type BatchGravityVolume = z.infer<typeof batchGravityVolumeSchema>;

/**
 * One fermentation gravity reading.
 *
 * A list rather than a pair of "start/end" fields because the shape of the
 * curve is the point: the plateau is how a brewer knows fermentation is
 * finished, and two readings 24 hours apart at the same gravity say more than
 * either alone.
 */
export const batchFermentationReadingSchema = z.strictObject({
  /** Stable id, unique within this batch's reading list. */
  readingId: documentIdSchema,
  /** When the sample was pulled, **ms since epoch**. */
  at: timestampSchema,
  /** Specific gravity. */
  sg: specificGravitySchema,
  /** Sample or fermenter temperature, **°C**, or `null`. */
  tempC: measuredTempCSchema.nullable(),
});

/** @see batchFermentationReadingSchema */
export type BatchFermentationReading = z.infer<typeof batchFermentationReadingSchema>;

/**
 * What went into the kettle to move its pH, as the brewer poured it.
 *
 * A record of the dose, not a plan for one: the amount is what actually went in
 * — which is routinely the round number the syringe reads rather than the
 * 0.67 mL that was suggested — and the strength is what the bottle says. Both
 * are needed to read the buffering back out of the before-and-after pair, which
 * is what `observedKettleBuffering` in core does with them.
 */
export const batchKettleAcidSchema = z.strictObject({
  acid: acidIdSchema,
  /** Strength, **% w/w**, as sold. */
  concentrationPercent: percentSchema,
  /** How much went in, in {@link unit}. */
  amount: nonNegativeSchema.max(10_000),
  /** `mL` for a liquid acid, `g` for one weighed out. */
  unit: z.enum(['mL', 'g']),
});

/** @see batchKettleAcidSchema */
export type BatchKettleAcid = z.infer<typeof batchKettleAcidSchema>;

/**
 * The kettle's pH, before and after the brewer adjusted it.
 *
 * Three independently-nullable fields, because they are recorded at three
 * different moments and any one of them alone is still worth keeping: the
 * reading as the boil is coming up, what went in, and where it landed. Nothing
 * here is ever filled in from a prediction — a pH nobody measured is `null`,
 * and the screen says so rather than showing a number the brewer would take for
 * a reading.
 *
 * The second and third together are what calibrate the next brew: they say what
 * this brewer's kettle actually costs per pH unit, which is a far better figure
 * than any model can produce from a recipe.
 */
export const batchKettleMeasurementSchema = z.strictObject({
  /** What the meter read on the wort in the kettle, before any acid. */
  preBoilPh: measuredPhSchema.nullable(),
  /** What it read once the acid was stirred through. */
  afterAdjustmentPh: measuredPhSchema.nullable(),
  /** What went in, or `null` when nothing did. */
  acid: batchKettleAcidSchema.nullable(),
});

/** @see batchKettleMeasurementSchema */
export type BatchKettleMeasurement = z.infer<typeof batchKettleMeasurementSchema>;

/** A kettle nobody has read a meter on yet. */
export function emptyBatchKettleMeasurement(): BatchKettleMeasurement {
  return { preBoilPh: null, afterAdjustmentPh: null, acid: null };
}

/**
 * Everything the brewer measured.
 *
 * All nullable, all independent, none required to reach any status — a batch
 * with no measurements at all is a perfectly good record of "I brewed this".
 * Each number that *is* present unlocks a specific derived figure in
 * `bridge/actuals.ts`, and nothing else changes.
 *
 * `fermentationReadings` is the one non-scalar and is **not** reachable through
 * `batch.updateMeasurements`: a list needs add/remove with stable ids to stay
 * invertible, so it has its own two commands.
 */
export const batchMeasurementsFieldsSchema = z.strictObject({
  /** Into the kettle, after the sparge. */
  preBoil: batchGravityVolumeSchema,
  /** Out of the kettle, after the boil (hot or corrected — the brewer's own reading). */
  postBoil: batchGravityVolumeSchema,
  /** Original gravity as pitched, or `null`. */
  originalGravity: specificGravitySchema.nullable(),
  /** Wort into the fermenter, **litres**, or `null`. */
  fermenterVolumeL: measuredVolumeSchema.nullable(),
  /** Beer into keg/bottles, **litres**, or `null`. */
  packagedVolumeL: measuredVolumeSchema.nullable(),
  /** Final gravity at packaging, or `null`. */
  finalGravity: specificGravitySchema.nullable(),
  /** Fermentation gravity samples, in the order they were added. */
  fermentationReadings: z.array(batchFermentationReadingSchema).max(500),
  /** The kettle's pH, before and after the brewer adjusted it. */
  kettle: batchKettleMeasurementSchema,
});

/**
 * Read measurements saved before the kettle group existed.
 *
 * Filled with three `null`s, which is the truth for every batch written before
 * there was anywhere to write a pH. A *migration* rather than a `.default()` on
 * the key: `batchMeasurementChangesSchema` is built by `.partial()`-ing this
 * shape, and `.partial()` does not strip a `.default()` — the key would
 * materialise on every measurement patch that omitted it and blank a brewer's
 * readings the moment they typed a gravity. `equipment.ts`'s `DECOCTION_FIELDS`
 * states the same rule from the other side.
 *
 * Idempotent, so it is safe on every read.
 */
export function migrateBatchMeasurements(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  if ('kettle' in rec) return raw;
  return { ...rec, kettle: emptyBatchKettleMeasurement() };
}

/** @see batchMeasurementsFieldsSchema */
export const batchMeasurementsSchema = z.preprocess(
  migrateBatchMeasurements,
  batchMeasurementsFieldsSchema,
);

/** @see batchMeasurementsSchema */
export type BatchMeasurements = z.infer<typeof batchMeasurementsFieldsSchema>;

/** A batch with nothing measured yet. */
export function emptyBatchMeasurements(): BatchMeasurements {
  return {
    preBoil: { sg: null, volumeL: null },
    postBoil: { sg: null, volumeL: null },
    originalGravity: null,
    fermenterVolumeL: null,
    packagedVolumeL: null,
    finalGravity: null,
    fermentationReadings: [],
    kettle: emptyBatchKettleMeasurement(),
  };
}

// ---------------------------------------------------------------------------
// Stage notes
// ---------------------------------------------------------------------------

/**
 * Prose, one field per stage.
 *
 * A strict object with every key present rather than a sparse record, so the
 * document JSON-round-trips to a `toStrictEqual`-identical value like every
 * other document in this package. Six always-present empty strings is a
 * rounding error next to a `recipeSnapshot`.
 */
export const batchStageNotesSchema = z.strictObject({
  planned: notesSchema,
  'brew-day': notesSchema,
  fermenting: notesSchema,
  conditioning: notesSchema,
  packaged: notesSchema,
  archived: notesSchema,
});

/** @see batchStageNotesSchema */
export type BatchStageNotes = z.infer<typeof batchStageNotesSchema>;

/** Empty prose for every stage. */
export function emptyBatchStageNotes(): BatchStageNotes {
  return {
    planned: '',
    'brew-day': '',
    fermenting: '',
    conditioning: '',
    packaged: '',
    archived: '',
  };
}

// ---------------------------------------------------------------------------
// The batch
// ---------------------------------------------------------------------------

/**
 * Brew-day step ids → the instant they were ticked.
 *
 * The *value* is the completion instant rather than `true` so the brew sheet
 * can print "Mash in — 09:14" after the fact, and so two devices ticking the
 * same step converge on a number rather than on a meaningless boolean. Step ids
 * are generated by the timeline builder from the snapshot and are stable for a
 * given snapshot, which is exactly why the snapshot must be frozen: a timeline
 * regenerated from an edited recipe would orphan every tick.
 */
export const batchCompletedStepsSchema = z.record(documentIdSchema, timestampSchema);

/**
 * Brew-day step ids → the instant their timer was started.
 *
 * Never "seconds remaining". See this module's header: an anchor is the only
 * representation that survives a reload, a backgrounded tab and a sync to the
 * brewer's other device. Remaining time is `startedAt + durationMs − now`,
 * computed at render.
 */
export const batchTimersSchema = z.record(documentIdSchema, timestampSchema);

/**
 * A cellar action the brewer put on the calendar themselves.
 *
 * Derived brew / dry-hop / package dates come from {@link BatchDoc.brewedAt}
 * plus the frozen fermentation schedule; these are the extras — a cold crash, a
 * transfer, a packaging day that is *not* "the day primary ends". `doneAt` is
 * the tick, the same shape as a brew-day step: an instant, not a boolean, so
 * two devices converge on a time.
 *
 * `starter` joined the list when the calendar gave building a starter its own
 * kind, so a brewer can put one on a day by hand for a batch whose recipe
 * carries none. Widening an enum is backward-compatible in the direction that
 * matters — every batch ever written parses against the longer list, since none
 * of them carries the new value — so {@link BATCH_SCHEMA_VERSION} does not move
 * for it, on the same rule `version.ts` states for a defaulted key. The reverse
 * direction, a client older than the deploy meeting a `starter` task, is the
 * ordinary cost of any additive change here: the snapshot fails validation and
 * is rebuilt from the command log, which is the authority anyway.
 */
export const batchCellarTaskKindSchema = z.enum([
  'starter',
  'dry-hop',
  'package',
  'transfer',
  'cold-crash',
  'other',
]);

/** @see batchCellarTaskKindSchema */
export type BatchCellarTaskKind = z.infer<typeof batchCellarTaskKindSchema>;

export const batchCellarTaskSchema = z.strictObject({
  taskId: documentIdSchema,
  kind: batchCellarTaskKindSchema,
  /** Display title, e.g. `Cold crash` or a free-text `other`. */
  title: nameSchema,
  /** When the action is due, **ms since epoch** (local-noon calendar day). */
  at: timestampSchema,
  /** Completion instant, or `null` while still outstanding. */
  doneAt: timestampSchema.nullable(),
  notes: notesSchema,
});

/** @see batchCellarTaskSchema */
export type BatchCellarTask = z.infer<typeof batchCellarTaskSchema>;

/**
 * One lot that left the cupboard for this brew, with the unit cost it carried
 * at deduct time.
 *
 * `lineCost` is `null` when the lot had no price; the snapshot still records
 * the movement so the batch page can say which ingredients were unpriced.
 */
export const batchCostLineSchema = z.strictObject({
  inventoryItemId: documentIdSchema,
  /**
   * Which of the brewer's own ingredients the consumed lot was a lot of, or
   * `null`. Optional as well as nullable: a snapshot frozen before links
   * existed has no such key and must still parse. It is what lets a lot that
   * was finished and deleted come back on return still linked (D-IL8).
   */
  ingredientId: documentIdSchema.nullable().optional(),
  name: nameSchema,
  kind: inventoryKindSchema,
  quantity: nonNegativeSchema,
  unit: z.enum(['g', 'packs']),
  unitCost: z.number().finite().nonnegative().nullable(),
  lineCost: z.number().finite().nonnegative().nullable(),
});

/** @see batchCostLineSchema */
export type BatchCostLine = z.infer<typeof batchCostLineSchema>;

/**
 * Frozen COGS for one batch, written by `batch.setInventoryDeducted`.
 *
 * `total` is the sum of priced lines only. `unpriced` is true when any
 * consumed lot had no unit cost, or the cupboard could not cover the recipe
 * (shortfalls) — the snapshot is then incomplete rather than wrong.
 */
export const batchCostSnapshotSchema = z.strictObject({
  currency: currencyCodeSchema,
  lines: z.array(batchCostLineSchema).max(500),
  total: z.number().finite().nonnegative(),
  unpriced: z.boolean(),
});

/** @see batchCostSnapshotSchema */
export type BatchCostSnapshot = z.infer<typeof batchCostSnapshotSchema>;

/**
 * The culture this batch is using, from the fridge to the fermenter.
 *
 * The second half of the repitch lineage: an inventory item records that a jar
 * of slurry came *out* of a batch, and this records that a culture went *into*
 * one. Both halves are needed, and neither is derivable from the other — a
 * brewer can pitch a pack that was never in the cupboard, and can harvest from
 * a batch whose pitch was never logged.
 *
 * ## The record begins when the jar is chosen, not when it is pitched
 *
 * A starter consumes the pack days before brew day, so the moment the jar
 * leaves the fridge is the moment the batch takes it: {@link takenAt} is that
 * moment and {@link pitchedAt} is nullable, because a chosen culture that has
 * not gone into wort yet is a real state with a real record. A brewer who
 * pitches straight from the pack still records both at once. Records written
 * before this field existed carry no {@link takenAt}; see {@link yeastTakenAt}
 * for what that means.
 *
 * ## What it buys
 *
 * The generation count, and no longer only that. Harvesting from a batch asks
 * this field what went in and adds one, which is the bookkeeping the lineage
 * exists to remove. Alongside it the record carries the pack facts a starter is
 * sized from — form, packs, cells and grams per pack, manufacture date — and
 * the starter itself, in the same shape and for the same reasons the recipe's
 * yeast row carries one: the inputs, never the yield. No cell count and no
 * viability is stored here, ever; both are recomputed by the model that
 * produced them.
 *
 * Snapshotted, not referenced, for the reason the whole document is: the lot it
 * came from is consumed by the take and may be gone, and "pitched gen 3 of
 * WLP830" has to stay true afterwards. That is also why the pack facts are
 * copied rather than read back off the lot.
 */
export const batchPitchedYeastSchema = z.strictObject({
  /**
   * The stocked lot it came out of, or `null` for a pack that was never in
   * inventory. A back-link, never dereferenced.
   */
  inventoryItemId: documentIdSchema.nullable(),
  /** The culture's name at pitch time, e.g. `WLP830 German Lager`. */
  name: nameSchema,
  /**
   * Ferments it had already been through. `0` is a lab pack, which is
   * `LAB_PACK_GENERATION` in `./inventory` and the one place that convention is
   * written down.
   */
  generation: z.number().int().min(0).max(1000),
  /** The harvest date it carried, `YYYY-MM-DD`, or `null` for a bought pack. */
  harvestedAt: isoDateSchema.nullable(),
  /** Packaging form, which decides the viability model, or `null` when unknown. */
  form: z.enum(['liquid', 'dry']).nullable().default(null),
  /** How many packs, vials or jars were taken. */
  packs: z.number().finite().gt(0).max(100).default(1),
  /** Viable cells per pack at manufacture, **billion**, or `null` for core's default. */
  cellsPerPackBillion: z.number().finite().min(0).max(1000).nullable().default(null),
  /** Grams per dry sachet, or `null`. */
  gramsPerPack: z.number().finite().min(0).max(5000).nullable().default(null),
  /** The pack's manufacture date, `YYYY-MM-DD`, or `null`. For viability decay. */
  manufactureDate: isoDateSchema.nullable().default(null),
  /** When the jar left the fridge, **ms since epoch**, or `null`. @see yeastTakenAt */
  takenAt: timestampSchema.nullable().default(null),
  /**
   * The starter built for this jar, or `null` when it goes straight into the
   * wort. Same shape as the recipe row's, and once a culture is chosen this one
   * is the whole truth: the recipe's starter is the planning default and is not
   * consulted again for this batch.
   */
  starter: RECIPE_YEAST_STARTER_FIELD.default(null),
  /** When it went into the wort, **ms since epoch**, or `null` if it has not yet. */
  pitchedAt: timestampSchema.nullable(),
});

/** @see batchPitchedYeastSchema */
export type BatchPitchedYeast = z.infer<typeof batchPitchedYeastSchema>;

/**
 * When this batch took the jar.
 *
 * {@link BatchPitchedYeast.takenAt} when it has one, and the pitch instant
 * otherwise: a record written before `takenAt` existed was taken at the moment
 * it was pitched, which is the honest reading of a log that only ever knew
 * about one of the two moments. `null` only when neither is set.
 */
export function yeastTakenAt(record: BatchPitchedYeast): number | null {
  return record.takenAt ?? record.pitchedAt;
}
// ---------------------------------------------------------------------------
// Tasting
// ---------------------------------------------------------------------------

/**
 * The five sections of the BJCP 2021 score sheet, and what each is worth.
 *
 * The weights are the guideline's, not ours. Aroma and flavour carry two-thirds
 * of the sheet between them because that is where a beer is mostly judged, and
 * appearance is worth three because a beer that looks wrong and tastes right is
 * mostly right. They live in this package rather than in the UI because they
 * **bound the schema**: 14 out of 12 for aroma is not a strong opinion, it is a
 * typo, and a second copy of the maxima in a component would be a second copy
 * that could disagree with the validator.
 */
export const BATCH_TASTING_SECTION_MAX = {
  aroma: 12,
  appearance: 3,
  flavor: 20,
  mouthfeel: 5,
  overall: 10,
} as const;

/** The section keys, in the order the sheet prints them. */
export const BATCH_TASTING_SECTIONS = [
  'aroma',
  'appearance',
  'flavor',
  'mouthfeel',
  'overall',
] as const satisfies readonly (keyof typeof BATCH_TASTING_SECTION_MAX)[];

/** @see BATCH_TASTING_SECTIONS */
export type BatchTastingSection = (typeof BATCH_TASTING_SECTIONS)[number];

/** What a full sheet comes to. Summed rather than written `50`, so it cannot drift. */
export const BATCH_TASTING_MAX_TOTAL: number = BATCH_TASTING_SECTIONS.reduce(
  (sum, section) => sum + BATCH_TASTING_SECTION_MAX[section],
  0,
);

/**
 * One section's score, or `null` for a section nobody filled in.
 *
 * Half points, because judges award them and a schema that refused would push a
 * brewer into rounding their own opinion. Nothing finer than a half: a sheet
 * scored to a tenth of a point is a spreadsheet pretending to be a palate.
 */
function tastingSectionSchema(max: number) {
  return z.number().finite().min(0).max(max).multipleOf(0.5).nullable();
}

/**
 * The descriptor checkboxes along the foot of the BJCP 2021 sheet.
 *
 * **Descriptors, not verdicts.** Esters are the point of a hefeweizen and
 * phenols are the point of a saison; the sheet asks the taster to say what is
 * *there*, and whether it belongs is what the five sections score. Keeping them
 * a flat set rather than faults-with-a-severity preserves that distinction, and
 * keeps what we store equal to the tick a brewer can make in one second.
 */
export const batchTastingDescriptorSchema = z.enum([
  'acetaldehyde',
  'alcoholic',
  'astringent',
  'diacetyl',
  'dms',
  'estery',
  'grassy',
  'light-struck',
  'metallic',
  'musty',
  'oxidized',
  'phenolic',
  'solvent',
  'sour',
  'spicy',
  'sulfur',
  'vegetal',
  'yeasty',
]);

/** @see batchTastingDescriptorSchema */
export type BatchTastingDescriptor = z.infer<typeof batchTastingDescriptorSchema>;

/** Every descriptor, in the sheet's own order. */
export const BATCH_TASTING_DESCRIPTORS: readonly BatchTastingDescriptor[] =
  batchTastingDescriptorSchema.options;

/**
 * How the beer actually tasted, on the score sheet's own terms.
 *
 * ## Why a score sheet, and not five stars
 *
 * Everything else on this document is a record of *process*: what went into the
 * kettle, what the hydrometer said, when the timer started. None of it can
 * answer the only question a brewer really has, which is whether the beer was
 * any good. A five-star rating would answer it and then be useless the moment
 * anything is asked of the answer. "3 out of 5" beside a brewhouse efficiency is
 * a number with nothing to correlate against, and it is a scale we would have
 * had to invent.
 *
 * The catalog already ships BJCP 2021 for the style bars, so the vocabulary is
 * in the product already: five weighted sections summing to fifty, plus the
 * sheet's descriptor row. Borrowing it means a brewer's own note reads to
 * anybody who has ever filled a competition sheet in, and it means the outcome
 * axis on the insights page is a scale somebody else validated.
 *
 * ## Every section is nullable, and the total is derived
 *
 * A tasting with nothing but a date and a paragraph is a perfectly good record
 * of "I drank this, and here is what I thought", so no section is required. The
 * total is not stored at all: {@link batchTastingTotal} computes it, and refuses
 * to compute it from a half-filled sheet. A stored total beside the five
 * sections that produce it is two truths about one fact, and they drift.
 *
 * ## Nothing in here carries a `.default()`, deliberately
 *
 * `batch.updateTasting` patches one of these by `.partial()`-ing this schema,
 * and **`.partial()` does not strip a `.default()`**: a defaulted key would
 * materialise in every patch that omits it and quietly overwrite what the
 * brewer ticked. `recipeFermentationStepChangesSchema` and `DECOCTION_FIELDS`
 * are both repairs for exactly that, so this object is written without a default
 * in the first place rather than repaired later. The backward-compatible default
 * that lets a batch saved last week parse belongs one level up, on
 * {@link batchDocSchema}'s `tastings`, where no `.partial()` can reach it.
 */
export const batchTastingSchema = z.strictObject({
  /** Stable id, unique within this batch's tasting list. */
  tastingId: documentIdSchema,
  /** When the beer was tasted, **ms since epoch** (local-noon calendar day). */
  at: timestampSchema,
  /** Aroma, 0–12, or `null` if unscored. */
  aroma: tastingSectionSchema(BATCH_TASTING_SECTION_MAX.aroma),
  /** Appearance, 0–3, or `null` if unscored. */
  appearance: tastingSectionSchema(BATCH_TASTING_SECTION_MAX.appearance),
  /** Flavour, 0–20, or `null` if unscored. Keyed the American way, as `srmToHex` is. */
  flavor: tastingSectionSchema(BATCH_TASTING_SECTION_MAX.flavor),
  /** Mouthfeel, 0–5, or `null` if unscored. */
  mouthfeel: tastingSectionSchema(BATCH_TASTING_SECTION_MAX.mouthfeel),
  /** Overall impression, 0–10, or `null` if unscored. */
  overall: tastingSectionSchema(BATCH_TASTING_SECTION_MAX.overall),
  /** Descriptors ticked on the sheet. @see batchTastingDescriptorSchema */
  descriptors: z.array(batchTastingDescriptorSchema).max(BATCH_TASTING_DESCRIPTORS.length),
  /** The taster's own words. */
  notes: notesSchema,
});

/** @see batchTastingSchema */
export type BatchTasting = z.infer<typeof batchTastingSchema>;

/** A blank sheet: dated, unscored, nothing ticked, nothing written. */
export function blankBatchTasting(args: {
  readonly tastingId: string;
  readonly at: number;
}): BatchTasting {
  return {
    tastingId: args.tastingId,
    at: args.at,
    aroma: null,
    appearance: null,
    flavor: null,
    mouthfeel: null,
    overall: null,
    descriptors: [],
    notes: '',
  };
}

/**
 * The sheet's total out of fifty, or `null` while any section is unscored.
 *
 * All five or nothing. A total is a fraction of fifty, and a sheet with aroma
 * left blank is not out of fifty — summed anyway it would read as a mediocre
 * beer rather than as an unfinished note, which is the one arithmetic mistake in
 * this feature that nobody would ever catch by eye. The insights page keys its
 * outcome axis off this, so "unscored" has to be a distinguishable answer rather
 * than a low one.
 */
export function batchTastingTotal(tasting: BatchTasting): number | null {
  let total = 0;
  for (const section of BATCH_TASTING_SECTIONS) {
    const score = tasting[section];
    if (score === null) return null;
    total += score;
  }
  return total;
}

/**
 * BJCP's own words for a total, from the band table at the foot of the sheet.
 *
 * Descending on `min`, and read by the first band a total clears, so the table
 * is the whole rule and there is no chain of comparisons to get a boundary wrong
 * in. The bands are the guideline's: a 44 is Excellent and a 45 is Outstanding,
 * whatever anybody thinks of that one point.
 */
export const BATCH_TASTING_BANDS: readonly { readonly min: number; readonly label: string }[] = [
  { min: 45, label: 'Outstanding' },
  { min: 38, label: 'Excellent' },
  { min: 30, label: 'Very good' },
  { min: 21, label: 'Good' },
  { min: 14, label: 'Fair' },
  { min: 0, label: 'Problematic' },
];

/** What BJCP calls a total. @see BATCH_TASTING_BANDS */
export function batchTastingBand(total: number): string {
  return BATCH_TASTING_BANDS.find((band) => total >= band.min)?.label ?? 'Problematic';
}
// ---------------------------------------------------------------------------
// Photographs
// ---------------------------------------------------------------------------

/**
 * How many photographs one batch may carry (PLAN-COMPETITIVE C4).
 *
 * Twelve, which is a brew day, a fermenter, a colour check and a glass with
 * room to spare, and is short of somebody emptying a camera roll into a
 * document that syncs to every device they own. It is enforced by
 * `batch.addPhoto` rather than only by the `.max()` on the array below, because
 * the array bound is a *parse* rule and a command that is refused has to be
 * refused before it reaches storage, not after a round trip through JSON.
 */
export const BATCH_MAX_PHOTOS = 12;

/**
 * What a stored photograph may be, and it is the same three-format list a
 * support screenshot may be.
 *
 * JPEG because that is what a phone camera produces, PNG because that is what a
 * screenshot of a fermentation chart is, and WebP because that is what this
 * app's own client re-encodes to before it uploads. No HEIC (nothing but Safari
 * decodes it), no GIF, and no SVG — an SVG is a document with script in it, and
 * these are served back from the app's own origin.
 *
 * Recorded on the document so the client knows what it is pointing an `<img>`
 * at without a request. It is **not** what the serve route trusts: the Worker
 * sniffs the magic bytes on the way in and serves the object's own stored type
 * on the way out, so a document edited by hand cannot change how a browser
 * treats bytes that are already in R2.
 */
export const batchPhotoContentTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);

/** @see batchPhotoContentTypeSchema */
export type BatchPhotoContentType = z.infer<typeof batchPhotoContentTypeSchema>;

/**
 * One photograph on a batch: where to find it, and what the brewer said it is.
 *
 * **The bytes are not here.** They are one R2 object, and the only thing that
 * addresses it is `photoId` — the Worker builds the key from the *session's*
 * user id plus the batch and photo ids, so this document names an image without
 * naming a place, and a document copied into somebody else's workspace
 * (impossible today, but the shape should not depend on that) points at
 * nothing rather than at somebody else's photograph. See
 * `apps/web/src/worker/batch-photos.ts`.
 *
 * That split is also why the fields that are here are here. `contentType` and
 * `bytes` are what a grid needs to lay out and to say what a batch is costing
 * without fetching a single image, and `caption` is the only part a brewer
 * actually types.
 *
 * No `.default()` on any key. The array itself is defaulted (see
 * {@link batchDocSchema}) so a batch written before photographs existed still
 * parses; an *item* with a defaulted key would materialise that key in any
 * `.partial()` change set derived from this schema and quietly overwrite what
 * the brewer wrote. `recipeFermentationStepChangesSchema` in
 * `commands/payloads.ts` is what that mistake costs to fix afterwards.
 */
export const batchPhotoSchema = z.strictObject({
  /** Stable id, unique within this batch's photo list. Also the R2 key's last segment. */
  photoId: documentIdSchema,
  /** What the Worker sniffed the bytes as. */
  contentType: batchPhotoContentTypeSchema,
  /** How large the stored object is, in bytes, as the Worker measured it. */
  bytes: z
    .number()
    .int()
    .min(1)
    .max(64 * 1024 * 1024),
  /** One line about what this shows. Empty is the ordinary case. */
  caption: z.string().max(280),
  /** When it was uploaded, **ms since epoch**. */
  addedAt: timestampSchema,
});

/** @see batchPhotoSchema */
export type BatchPhoto = z.infer<typeof batchPhotoSchema>;

/**
 * How this batch is being packaged, where it differs from the plan
 * (PLAN-BATCH-PACKAGING D-BP1).
 *
 * Each of the three is `null` until the brewer changes it on the batch, and
 * `null` means "as the recipe planned", read from {@link BatchDoc.recipeSnapshot}.
 * A copy of all three taken at brew time was the alternative, and it would
 * make "planned bottles, kegged instead" invisible: every batch would look
 * decided afresh. Read the answer through {@link effectiveBatchPackaging},
 * never field by field.
 *
 * The bounds on `carbonationVolumes` are the recipe's.
 */
export const batchPackagingSchema = z.strictObject({
  method: packagingMethodSchema.nullable().default(null),
  primingSugar: primingSugarSchema.nullable().default(null),
  carbonationVolumes: z.number().finite().min(0).max(6).nullable().default(null),
});

/** @see batchPackagingSchema */
export type BatchPackaging = z.infer<typeof batchPackagingSchema>;

/** A batch that has changed nothing about its packaging. */
export function emptyBatchPackaging(): BatchPackaging {
  return { method: null, primingSugar: null, carbonationVolumes: null };
}

/** A persisted batch. */
export const batchDocSchema = z.strictObject({
  /** Document schema version; see {@link BATCH_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Stable batch id, unique within the workspace. */
  id: documentIdSchema,
  /**
   * Display name, e.g. `Fixture Pale Ale — 15 Aug 2026`.
   *
   * The caller supplies the whole formatted string. This package has no clock
   * and no locale, and a date rendered here would be neither reproducible nor
   * in the brewer's format.
   */
  name: nameSchema,
  /**
   * The recipe this was brewed from, or `null` once that recipe is deleted.
   *
   * A back-link for "batches of this recipe", nothing more. Every number the
   * batch reports comes from {@link recipeSnapshot}.
   */
  recipeId: documentIdSchema.nullable(),
  /** The recipe **as it was at batch creation**. Frozen. See the module header. */
  recipeSnapshot: recipeDocSchema,
  /** The resolved equipment profile **as it was at batch creation**. Frozen. */
  equipmentSnapshot: equipmentProfileDocSchema,
  /** Where the batch is in its life. */
  status: batchStatusSchema,
  /** When the brew day happened, **ms since epoch**, or `null` while still planned. */
  brewedAt: timestampSchema.nullable(),
  /**
   * The day the beer went into bottles or a keg, **YYYY-MM-DD**, or `null` if
   * nobody has said.
   *
   * A day rather than an instant, like a blend's `stageDates.packaged`. Without
   * it the calendar's package day is derived from {@link brewedAt} plus the
   * fermentation schedule, which is only the plan. Stamped when the status moves
   * to `packaged` and editable after. `.default(null)` so a batch written before
   * the field existed still parses.
   */
  packagedOn: isoDateSchema.nullable().default(null),
  /** What the brewer measured. */
  measurements: batchMeasurementsSchema,
  /** Brew-day checklist ticks. */
  completedSteps: batchCompletedStepsSchema,
  /** Brew-day timer anchors. */
  timers: batchTimersSchema,
  /** Prose, per stage. */
  stageNotes: batchStageNotesSchema,
  /**
   * When this batch's ingredients were taken out of inventory, **ms since
   * epoch**, or `null` if they have not been.
   *
   * The **idempotence guard** for deduct-on-brew, and the reason double
   * deduction is structurally impossible rather than merely discouraged:
   * `planInventoryDeduction` returns an empty plan whenever this is set, so the
   * second press of the button produces no commands at all — there is nothing
   * to dispatch, and therefore nothing for a race, a re-render or a synced
   * second device to apply twice.
   *
   * Nullable and `.default(null)` so a batch written before the field existed
   * still parses as "not deducted", which is the truth for every one of them.
   * Undoing a deduction clears it through `batch.setInventoryDeducted`, which is
   * what makes the whole operation one exactly-invertible step.
   */
  inventoryDeductedAt: timestampSchema.nullable().default(null),
  /**
   * Scheduled cellar actions on this batch.
   *
   * Defaulted to `[]` so a batch written before the field existed still parses
   * — the same backward-compatible addition {@link inventoryDeductedAt} made.
   */
  cellarTasks: z.array(batchCellarTaskSchema).default([]),
  /**
   * What this brew cost, frozen at the moment inventory was deducted.
   *
   * A record, not a live estimate: repricing a sack afterwards must not move
   * last week's batch, the same snapshot rule {@link recipeSnapshot} follows.
   * `null` until deducted (or after the deduction is undone). `.default(null)`
   * so a batch written before costing still parses.
   */
  costSnapshot: batchCostSnapshotSchema.nullable().default(null),
  /**
   * The yeast this batch was pitched with, or `null` when it was not recorded.
   *
   * Written by `batch.setPitchedYeast`, and read by the harvest flow to work
   * out what generation comes next. `.default(null)` so a batch written before
   * lineage existed still parses; a harvest off one of those starts at
   * generation 1, which is the right answer for a batch nobody said was a
   * repitch.
   */
  pitchedYeast: batchPitchedYeastSchema.nullable().default(null),
  /**
   * What the beer tasted like, oldest note first.
   *
   * A list rather than one record, because beer changes: a hoppy pale at three
   * weeks and the same keg at three months are two honest answers, and keeping
   * only the latest would throw away the fact that it faded. Insights reads the
   * most recent scored note, which is the current truth about the beer.
   *
   * Defaulted to `[]` so a batch written before this field existed still parses,
   * the same backward-compatible addition {@link batchDocSchema}'s
   * `inventoryDeductedAt` and `cellarTasks` made. That is why
   * {@link BATCH_SCHEMA_VERSION} did not move for it.
   */
  tastings: z.array(batchTastingSchema).max(100).default([]),
  /**
   * Photographs of this brew, oldest first (PLAN-COMPETITIVE C4).
   *
   * Descriptors only; the bytes are R2 objects the Worker addresses from the
   * session. `.default([])` so a batch written before photographs existed still
   * parses as one with none, which is the truth for every one of them and is
   * the same backward-compatible addition {@link inventoryDeductedAt} and
   * `cellarTasks` made. That default is why {@link BATCH_SCHEMA_VERSION} does
   * not move — see `version.ts`, which states the rule.
   */
  photos: z.array(batchPhotoSchema).max(BATCH_MAX_PHOTOS).default([]),
  /**
   * The packaging choices changed on this batch, each `null` while it follows
   * the plan. `.default` so a batch written before this existed follows its
   * plan in all three, which is what it was doing; {@link BATCH_SCHEMA_VERSION}
   * does not move, for the reason `photos` gives.
   */
  packaging: batchPackagingSchema.default(emptyBatchPackaging),
  /** Creation time, **ms since epoch**, from the creating command's envelope. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**, from the last command's envelope. */
  updatedAt: timestampSchema,
});

/** @see batchDocSchema */
export type BatchDoc = z.infer<typeof batchDocSchema>;

/** The packaging a batch is actually getting. @see effectiveBatchPackaging */
export interface EffectiveBatchPackaging {
  readonly method: PackagingMethod;
  readonly primingSugar: PrimingSugarId;
  readonly carbonationVolumes: number;
  /** Which of the three the batch changed; the rest are the recipe's plan. */
  readonly fromBatch: {
    readonly method: boolean;
    readonly primingSugar: boolean;
    readonly carbonationVolumes: boolean;
  };
}

/**
 * The batch's packaging, its own choices over the recipe's plan (D-BP3).
 *
 * The one place the override is resolved: the packaging panel, the keg
 * weigh-in card and anything later read this, so they cannot disagree.
 */
export function effectiveBatchPackaging(batch: BatchDoc): EffectiveBatchPackaging {
  const { packaging, recipeSnapshot } = batch;
  return {
    method: packaging.method ?? recipeSnapshot.packagingMethod,
    primingSugar: packaging.primingSugar ?? recipeSnapshot.primingSugar,
    carbonationVolumes: packaging.carbonationVolumes ?? recipeSnapshot.carbonationVolumes,
    fromBatch: {
      method: packaging.method !== null,
      primingSugar: packaging.primingSugar !== null,
      carbonationVolumes: packaging.carbonationVolumes !== null,
    },
  };
}

// ---------------------------------------------------------------------------
// Drift, and the one window in which the freeze can be re-taken
// ---------------------------------------------------------------------------

/**
 * Whether the recipe has moved on since this batch froze its copy.
 *
 * Two timestamps, and deliberately no cleverer than that — the same shape and
 * the same honest over-report as `listingIsStale` in the gallery protocol. A
 * document's `updatedAt` moves on every command, so this answers "has anything
 * at all happened to the recipe since the batch was planned", not "would the
 * brew sheet come out different". The second question needs a recipe diff, and
 * a false "yes" here costs a brewer one glance at {@link batchResnapshotRefusal}
 * and a no-op re-take, while a false "no" costs them the wrong beer.
 *
 * `undefined` recipe — deleted, or a dangling back-link — is **not** drift.
 * There is nothing to re-take from, and the batch is by design still whole.
 *
 * @param batch - The batch holding the frozen copy.
 * @param recipe - The live recipe, or `undefined` if it is gone.
 */
export function batchRecipeHasDrifted(
  batch: BatchDoc,
  recipe: { readonly updatedAt: number } | undefined,
): boolean {
  return recipe !== undefined && recipe.updatedAt > batch.recipeSnapshot.updatedAt;
}

/** Why a batch cannot have its snapshots re-taken. @see batchResnapshotRefusal */
export type BatchResnapshotRefusal =
  /** Fermenting or later. The brew happened; records do not move. */
  | 'brewed'
  /** Steps are ticked or timers are running; re-taking would orphan them. */
  | 'brew-day-started'
  /** Ingredients are already out of inventory against the frozen grist. */
  | 'inventory-deducted'
  /** No live recipe to re-take from. */
  | 'recipe-gone';

/**
 * The statuses in which a batch might still be a plan rather than a record.
 *
 * `brew-day` is in the list, and that is not a mistake. The Brew dialog creates
 * a batch at `planned` when its brew date is after today and at `brew-day`
 * otherwise, so `planned` is now an ordinary way for a batch to start rather
 * than somewhere only the stepper can reach. `brew-day` stays in the list all
 * the same: a batch created for today, or dragged back by the stepper, may
 * still be one nobody has started.
 *
 * Being at `brew-day` therefore says almost nothing about whether the brew has
 * started. What the *evidence* below says is dispositive.
 */
const BATCH_REPLANNABLE_STATUSES: readonly BatchStatus[] = ['planned', 'brew-day'];

/**
 * Whether this batch's frozen copies may be re-taken, and if not, why.
 *
 * ## Why there is a window at all
 *
 * The snapshot rule (see the module header) is written about **records**:
 * "editing the source recipe afterwards cannot move a single number on a batch
 * that has already been brewed". A batch created for next Saturday is not a
 * record yet. Freezing it protects a history that has not happened, and the
 * brewer who tweaks the recipe on Friday then brews Tuesday's beer with nothing
 * anywhere saying the copy had gone stale.
 *
 * ## The window closes on evidence, not on status
 *
 * A batch becomes a record the moment something is recorded against it, and
 * each refusal below names one such thing. Deliberately **not** "is `brewedAt`
 * in the past": this package has no clock, and a brew date is a brewer's
 * intention rather than an observation — it is routinely set to a day that has
 * not happened, and just as routinely left at today's date on a batch nobody
 * has started.
 *
 * - **`brewed`** — fermenting or later. There is beer; the record exists.
 * - **`brew-day-started`** — `completedSteps` / `timers` are keyed by step ids
 *   the timeline builder derives *from the snapshot*, so re-taking would orphan
 *   every tick. This is the structural reason the freeze cannot simply be
 *   dropped, and it is checked independently of `status` because a brewer can
 *   move a batch back to `planned` after ticking something.
 * - **`inventory-deducted`** — the grist already left the shelves against the
 *   frozen recipe, and `costSnapshot` was priced off it. Re-taking would make
 *   the deduction describe ingredients this batch no longer calls for. Undo the
 *   deduction first and the window reopens.
 * - **`recipe-gone`** — deleted, or the back-link never pointed anywhere.
 *
 * Returns `null` when nothing has been recorded yet and the copies may move.
 *
 * One function, used by the command's guard and by the button that offers it,
 * so the UI can never propose something the reducer will refuse.
 */
export function batchResnapshotRefusal(
  batch: BatchDoc,
  recipe: { readonly id: string } | undefined,
): BatchResnapshotRefusal | null {
  if (recipe === undefined || batch.recipeId === null || batch.recipeId !== recipe.id) {
    return 'recipe-gone';
  }
  if (!BATCH_REPLANNABLE_STATUSES.includes(batch.status)) return 'brewed';
  if (Object.keys(batch.completedSteps).length > 0 || Object.keys(batch.timers).length > 0) {
    return 'brew-day-started';
  }
  if (batch.inventoryDeductedAt !== null) return 'inventory-deducted';
  return null;
}

/**
 * Build the batch document a `batch.create` command carries.
 *
 * Pure: identity, name, timestamps and both snapshots all come from the caller.
 * The snapshots are copied structurally (spread) so a later mutation of the
 * source objects by a careless caller cannot reach into the batch — the reducer
 * itself never mutates, but this function is also called from UI code where the
 * guarantee is worth restating in one place.
 *
 * @param args - Identity, the two snapshots, and the creating envelope's time.
 */
export function buildBatchDoc(args: {
  readonly id: string;
  readonly name: string;
  readonly recipeId: string | null;
  readonly recipeSnapshot: RecipeDoc;
  readonly equipmentSnapshot: EquipmentProfileDoc;
  readonly status?: BatchStatus;
  readonly brewedAt?: number | null;
  readonly timestamp: number;
}): BatchDoc {
  return {
    schemaVersion: BATCH_SCHEMA_VERSION,
    id: args.id,
    name: args.name,
    recipeId: args.recipeId,
    recipeSnapshot: { ...args.recipeSnapshot },
    equipmentSnapshot: { ...args.equipmentSnapshot },
    status: args.status ?? 'planned',
    brewedAt: args.brewedAt ?? null,
    packagedOn: null,
    measurements: emptyBatchMeasurements(),
    completedSteps: {},
    timers: {},
    stageNotes: emptyBatchStageNotes(),
    inventoryDeductedAt: null,
    cellarTasks: [],
    costSnapshot: null,
    pitchedYeast: null,
    tastings: [],
    photos: [],
    packaging: emptyBatchPackaging(),
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}
